import { BrandWordmark } from "@manos/blocks";
import { can } from "@manos/core";
import { Link, Outlet, useNavigate, useRouteContext, useRouterState } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, mediaQuery, overviewQuery, pagesQuery, peopleQuery, worksQuery } from "../lib/api";
import { uploads } from "../lib/uploads";
import { initials } from "../lib/format";
import { useCreateWork } from "../lib/useCreateWork";
import { CommandMenu, type Command } from "./CommandMenu";
import { Icon, type IconName } from "./Icons";
import { BellContext, NotificationBell, NotificationPanel, PageBell } from "./Notifications";
import { TourProvider, startTour, useTour } from "./Tour";
import { DropZone } from "./DropZone";
import { UploadTray } from "./UploadTray";

const NAV = [
  { to: "/pages", label: "Pages", icon: "pages" },
  { to: "/works", label: "Works", icon: "works" },
  { to: "/media", label: "Media library", icon: "media" },
  { to: "/people", label: "People", icon: "people" },
  { to: "/inbox", label: "Inbox", icon: "inbox" },
] as const satisfies readonly { to: string; label: string; icon: IconName }[];

const RAIL_KEY = "manos.sidebar";
/** Whether the sidebar is folded to a strip of icons, remembered in this browser. ⌘\ or Ctrl+\ toggles it. */
function useRail() {
  const [rail, setRail] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === "rail";
    } catch {
      return false;
    }
  });
  const toggle = useCallback(() => {
    setRail((r) => {
      try {
        localStorage.setItem(RAIL_KEY, r ? "full" : "rail");
      } catch {
        /* private browsing: just don't remember */
      }
      return !r;
    });
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "\\" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle]);
  return { rail, toggle };
}

export function Shell() {
  const { user } = useRouteContext({ from: "/_app" });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const overview = useQuery(overviewQuery);
  // Phones: the sidebar is a slim bar with a Menu button; it closes whenever the page changes.
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [meOpen, setMeOpen] = useState(false);
  const closeNotif = useCallback(() => setNotifOpen(false), []);
  const bell = useMemo(() => ({ open: notifOpen, toggle: () => setNotifOpen((o) => !o) }), [notifOpen]);
  const { rail, toggle: toggleRail } = useRail();
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  // Small deterrents for anyone but admins: no "Save image/video as…" menu and no dragging media out.
  useEffect(() => {
    if (user.role === "admin") return;
    const block = (e: Event) => {
      if (e.target instanceof Element && e.target.closest("img, video, picture, .mb-player, .thumb, .scrub")) e.preventDefault();
    };
    document.addEventListener("contextmenu", block);
    document.addEventListener("dragstart", block);
    return () => {
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("dragstart", block);
    };
  }, [user.role]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: close the menus whenever the page changes
  useEffect(() => {
    setMenuOpen(false);
    setMeOpen(false);
  }, [pathname]);
  // Refresh the media library whenever an upload starts or finishes, from any screen.
  useEffect(() => uploads.onMediaChanged(() => void qc.invalidateQueries({ queryKey: mediaQuery.queryKey })), [qc]);
  const signOut = useMutation({
    mutationFn: () => api("/auth/logout", { method: "POST" }),
    onSettled: async () => {
      qc.clear();
      await navigate({ to: "/login", search: {} });
    },
  });
  // ⌘K can open any work, person or page by name, besides the built-in actions.
  const works = useQuery(worksQuery);
  const people = useQuery(peopleQuery);
  const pages = useQuery(pagesQuery);
  const createWork = useCreateWork();
  const extra = useMemo<Command[]>(
    () => [
      { id: "new-work", label: "New work", run: () => createWork.mutate() },
      { id: "upload", label: "Upload media", run: () => uploads.pick() },
      ...(works.data ?? []).map((w) => ({
        id: `w-${w.id}`,
        label: `Work: ${w.title}`,
        hint: { draft: "Draft", review: "In review", published: "Live", archived: "Archived" }[w.status],
        run: () => void navigate({ to: "/works/$id", params: { id: w.id } }),
      })),
      ...(people.data ?? []).map((p) => ({
        id: `p-${p.id}`,
        label: `Person: ${p.name}`,
        run: () => void navigate({ to: "/people/$id", params: { id: p.id } }),
      })),
      ...(pages.data ?? []).map((p) => ({
        id: `pg-${p.id}`,
        label: `Page: ${p.title.en ?? p.slug}`,
        run: () => void navigate({ to: "/pages/$slug", params: { slug: p.slug } }),
      })),
      { id: "tour", label: "Take the tour", run: () => startTour("full") },
      { id: "signout", label: "Sign out", run: () => signOut.mutate() },
    ],
    [signOut, navigate, createWork, works.data, people.data, pages.data],
  );

  const counts: Record<string, number> = {
    "/inbox": overview.data?.unread ?? 0,
    "/works": overview.data?.comments.works ?? 0,
    "/pages": overview.data?.comments.pages ?? 0,
  };
  const who = user.name ?? user.email;

  return (
    <TourProvider>
      <div className={`app ${rail ? "rail" : ""} ${user.role === "admin" ? "" : "protect-media"}`}>
        <a href="#main" className="skip">
          Skip to content
        </a>
        <aside className={`side ${menuOpen ? "open" : ""}`}>
          <div className="side-top">
            <div className="brand">
              <BrandWordmark />
              <span>Studio</span>
            </div>
            <div className="side-tools">
              {/* Phones: the bell sits in the top bar beside Menu. On wider screens it's top right of the page. */}
              <NotificationBell open={notifOpen} onToggle={() => setNotifOpen((o) => !o)} />
              <button type="button" className="menu-toggle" aria-expanded={menuOpen} aria-controls="side-body" onClick={() => setMenuOpen((o) => !o)}>
                {menuOpen ? "Close" : "Menu"}
              </button>
            </div>
          </div>
          <div className="side-body" id="side-body">
            <CommandMenu extra={extra} />
            <nav className="nav" aria-label="Sections">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="nav-i" activeProps={{ "aria-current": "page" }} title={rail ? n.label : undefined}>
                  <Icon name={n.icon} className="nav-icon" />
                  <span className="nav-label">{n.label}</span>
                  {counts[n.to] ? (
                    <small className="badge" title={n.to === "/inbox" ? "Unread messages" : "Unread comments"}>
                      {counts[n.to]}
                    </small>
                  ) : null}
                </Link>
              ))}
            </nav>
            <div className="grow" />
            {can.manageTeam(user.role) && (
              <Link to="/admin/team" className="nav-i" aria-current={pathname.startsWith("/admin") ? "page" : undefined} title={rail ? "Admin" : undefined}>
                <Icon name="settings" className="nav-icon" />
                <span className="nav-label">Admin</span>
              </Link>
            )}
            <MeMenu
              open={meOpen}
              setOpen={setMeOpen}
              who={who}
              role={user.role}
              avatarUrl={user.avatarUrl}
              onSignOut={() => signOut.mutate()}
              busy={signOut.isPending}
            />
            <button
              type="button"
              className="rail-toggle"
              onClick={toggleRail}
              aria-pressed={rail}
              title={rail ? "Expand the sidebar (⌘\\)" : "Collapse the sidebar (⌘\\)"}
            >
              <Icon name={rail ? "expand" : "collapse"} />
              <span className="nav-label">Collapse</span>
            </button>
          </div>
        </aside>
        {notifOpen && <NotificationPanel onClose={closeNotif} />}
        <main id="main" className="main">
          <BellContext.Provider value={bell}>
            <Outlet />
          </BellContext.Provider>
        </main>
        <DropZone />
        <UploadTray />
      </div>
    </TourProvider>
  );
}

/** "Take the tour", in your menu. */
function TourMenuItem({ onDone }: { onDone: () => void }) {
  const tour = useTour();
  if (!tour) return null;
  return (
    <button
      type="button"
      className="me-item"
      role="menuitem"
      onClick={() => {
        onDone();
        tour.start("full");
      }}
    >
      <Icon name="search" />
      Take the tour
    </button>
  );
}

/** You, at the foot of the sidebar: opens Your account and Sign out. */
function MeMenu({
  open,
  setOpen,
  who,
  role,
  avatarUrl,
  onSignOut,
  busy,
}: {
  open: boolean;
  setOpen: (o: boolean | ((o: boolean) => boolean)) => void;
  who: string;
  role: string;
  avatarUrl: string | null;
  onSignOut: () => void;
  busy: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, setOpen]);
  return (
    <div className="me-wrap" ref={box}>
      {open && (
        <div className="me-menu" role="menu" aria-label="Your account">
          <Link to="/account" className="me-item" role="menuitem" onClick={() => setOpen(false)}>
            <Icon name="account" />
            Your account
          </Link>
          <TourMenuItem onDone={() => setOpen(false)} />
          <button type="button" className="me-item" role="menuitem" onClick={onSignOut} disabled={busy}>
            <Icon name="signout" />
            Sign out
          </button>
        </div>
      )}
      <button type="button" className="me" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} title={who}>
        {avatarUrl ? <img src={avatarUrl} alt="" className="av" referrerPolicy="no-referrer" /> : <span className="av">{initials(who)}</span>}
        <span className="me-text">
          <span>{who}</span>
          <small>{role}</small>
        </span>
      </button>
    </div>
  );
}

export function PageHeader({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <header className="topbar">
      <h1>{title}</h1>
      <div className="topbar-actions">
        {children}
        <PageBell />
      </div>
    </header>
  );
}
